import { gsap } from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

ScrollTrigger.config({ ignoreMobileResize: true });

// One-time refresh after assets load
window.addEventListener("load", () => ScrollTrigger.refresh());

// Fade-in variants (desktop-only ≥992px)
(() => {
  const SELECTOR = [
    '[data-animate="fadeIn"]',
    '[data-animate="fadeInUp"]',
    '[data-animate="fadeInUpDesktop"]'
  ].join(", ");

  // Respect reduced motion
  if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      el.style.opacity = "1";
      el.style.visibility = "visible";
      el.style.transform = "none";
    });
    return;
  }

  // Save original inline styles so GSAP can restore on revert
  ScrollTrigger.saveStyles?.(SELECTOR);

  const mm = gsap.matchMedia();

  mm.add("(min-width: 992px)", () => {
    const ctx = gsap.context(() => {
      const els = document.querySelectorAll(SELECTOR);
      if (!els.length) return;

      els.forEach((el) => {
        const type = el.getAttribute("data-animate");
        const isUp = type === "fadeInUp" || type === "fadeInUpDesktop";

        gsap.set(el, isUp
          ? { autoAlpha: 0, y: 24, willChange: "opacity, transform" }
          : { autoAlpha: 0, willChange: "opacity" }
        );

        ScrollTrigger.create({
          trigger: el,
          start: "top 85%",
          invalidateOnRefresh: true,
          onEnter: () => {
            gsap.to(el, {
              autoAlpha: 1,
              y: isUp ? 0 : undefined,
              duration: 2.0,
              ease: "power2.out",
              overwrite: "auto",
              delay: parseFloat(el.getAttribute("data-animate-delay")) || 0
            });
          },
          onEnterBack: () => {
            gsap.to(el, {
              autoAlpha: 1,
              y: isUp ? 0 : undefined,
              duration: 1.6,
              ease: "power2.out",
              overwrite: "auto",
              delay: parseFloat(el.getAttribute("data-animate-delay")) || 0
            });
          }
        });
      });
    });

    // Cleanup when leaving ≥992px
    return () => ctx.revert();
  });
})();


// Parallax pinned section (desktop-only ≥992px)
(() => {
  const SELECTOR = '[data-animate="parallax"]';
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  ScrollTrigger.saveStyles?.([SELECTOR, `${SELECTOR} [data-parallax-target]`]);

  const mm = gsap.matchMedia();

  mm.add("(min-width: 992px)", () => {
    const ctx = gsap.context(() => {
      const parallaxEls = document.querySelectorAll(SELECTOR);
      const headerHeight = parseInt(getComputedStyle(document.documentElement).getPropertyValue('--site-header-height-fixed'), 10) || 0;
      if (!parallaxEls.length) return;

      parallaxEls.forEach((el) => {
        gsap.set(el, { minHeight: "calc(100vh - var(--site-header-height-fixed))", position: "relative", zIndex: 0 });

        if (reducedMotion) return;

        const target = el.querySelector("[data-parallax-target]");

        if (target) {
          gsap.to(target, {
            yPercent: -15,
            ease: "none",
            scrollTrigger: {
              trigger: el,
              start: `top top+=${headerHeight}`,
              end: "+=150%",
              scrub: true,
              pin: true,
              pinSpacing: false,
              anticipatePin: 1,
              invalidateOnRefresh: true
            }
          });
        } else {
          ScrollTrigger.create({
            trigger: el,
            start: `top top+=${headerHeight}`,
            end: "+=120%",
            pin: true,
            pinSpacing: false,
            anticipatePin: 1,
            invalidateOnRefresh: true
          });
        }
      });
    });

    // Kill triggers/pins and remove the inline styles we applied
    return () => {
      ctx.revert();
      document.querySelectorAll(SELECTOR).forEach((el) => {
        gsap.set(el, { clearProps: "minHeight,position,zIndex" });
      });
    };
  });
})();
